import { SIDEBAR_MENU as dict } from '@/resources/dict'
import { doLogout } from '@/proxy/auth/logout'
import { URLS } from '@/resources/enums/externalsUrls'
import { SidebarMenuItem } from '@/resources/types'
import { HexaIcons, SidebarMenuItems, userRoleEnums } from '@/resources/enums'
import { UseSeller } from '@/contexts/seller/types'
import { hasOnlinePaymentSectionEnabled } from '@/resources/services/permission/permissions'

const disableForNonTrainingOrOperating = (seller: UseSeller) =>
  !seller.isTrainingOrOperating

const HOME_SIDEBAR_ITEM: SidebarMenuItem = {
  text: dict.home,
  to: '/home',
  id: SidebarMenuItems.HOME,
  featureFlag: true,
  splitName: 'new_home_page',
  icon: HexaIcons.HOME,
}

const PENDING_SIDEBAR_ITEM: SidebarMenuItem = {
  text: dict.pendingOrders,
  to: '/poc-orders',
  id: SidebarMenuItems.PENDING,
  icon: HexaIcons.CLIPBOARD,
}

const DATES_AVAILABILITY: SidebarMenuItem = {
  text: dict.datesAvailability,
  to: '/dates-availability',
  id: SidebarMenuItems.DATES_AVAILABILITY,
  shouldDisable: (seller: UseSeller) => !seller.isPocCBE,
  icon: HexaIcons.CALENDAR,
}

const SCHEDULED_SIDEBAR_ITEM: SidebarMenuItem = {
  text: dict.scheduledOrders,
  to: '/poc-orders-scheduled',
  id: SidebarMenuItems.SCHEDULED,
  featureFlag: true,
  splitName: 'scheduled_orders_beta',
  tag: {
    text: dict.newTag,
  },
  icon: HexaIcons.CLOCK,
}

const MAP_SIDEBAR_ITEM: SidebarMenuItem = {
  text: dict.map,
  to: '/orders-map',
  id: SidebarMenuItems.MAP,
  icon: HexaIcons.MAP_PIN,
}

const HISTORY_SIDEBAR_ITEM: SidebarMenuItem = {
  text: dict.history,
  to: '/history',
  id: SidebarMenuItems.HISTORY,
  shouldDisable: disableForNonTrainingOrOperating,
  icon: HexaIcons.CLIPBOARD_PACKAGE,
}

const ORDERS_SIDEBAR_ITEM: SidebarMenuItem = {
  text: dict.orders,
  id: SidebarMenuItems.ORDERS,
  submenu: [
    DATES_AVAILABILITY,
    PENDING_SIDEBAR_ITEM,
    SCHEDULED_SIDEBAR_ITEM,
    MAP_SIDEBAR_ITEM,
    HISTORY_SIDEBAR_ITEM,
  ],
}

const INVENTORY_SIDEBAR_ITEM: SidebarMenuItem = {
  text: dict.products,
  to: '/inventory',
  id: SidebarMenuItems.INVENTORY,
  icon: HexaIcons.BEER_BOTTLES,
}

const ON_SALE_SIDEBAR_ITEM: SidebarMenuItem = {
  text: dict.onSale,
  to: '/on-sale',
  id: SidebarMenuItems.ON_SALE,
  featureFlag: true,
  splitName: 'enable_on_sales_page',
  shouldDisable: disableForNonTrainingOrOperating,
  icon: HexaIcons.TAG,
}

const STOCK_SIDEBAR_ITEM: SidebarMenuItem = {
  text: dict.stock,
  id: SidebarMenuItems.STOCK,
  submenu: [INVENTORY_SIDEBAR_ITEM, ON_SALE_SIDEBAR_ITEM],
  shouldDisable: (seller: UseSeller) =>
    seller.isRegistered || seller.isRejectedOrIvalidated,
}

const PERFORMANCE_SIDEBAR_ITEM: SidebarMenuItem = {
  text: dict.performance,
  to: '/performance',
  id: SidebarMenuItems.PERFORMANCE,
  shouldDisable: disableForNonTrainingOrOperating,
  icon: HexaIcons.TRENDING_UP,
}

const MANAGEMENT_SIDEBAR_ITEM: SidebarMenuItem = {
  text: dict.management,
  to: '/management',
  id: SidebarMenuItems.MANAGEMENT,
  shouldDisable: disableForNonTrainingOrOperating,
  icon: HexaIcons.WALLET,
}

const ONLINE_PAYMENT_SIDEBAR_ITEM: SidebarMenuItem = {
  text: dict.onlinePayment,
  to: '/online-payment',
  id: SidebarMenuItems.ONLINE_PAYMENT,
  shouldDisable: () => !hasOnlinePaymentSectionEnabled(),
  icon: HexaIcons.CURRENCY,
}

const FINANCIAL_REPORT_MENU_ITEM: SidebarMenuItem = {
  text: dict.financialReport,
  to: '/financial-report',
  id: SidebarMenuItems.FINANCIAL_REPORT,
  tag: {
    text: dict.newTag,
  },
  shouldDisable: (seller: UseSeller) => {
    return seller.role !== userRoleEnums.OWNER
  },
  icon: HexaIcons.BANK_TRANSFER,
}

const FINANCIAL_SIDEBAR_ITEM: SidebarMenuItem = {
  text: dict.financial,
  id: SidebarMenuItems.FINANCIAL,
  submenu: [
    MANAGEMENT_SIDEBAR_ITEM,
    FINANCIAL_REPORT_MENU_ITEM,
    ONLINE_PAYMENT_SIDEBAR_ITEM,
  ],
}

const FLEET_MANAGEMENT_SIDEBAR_ITEM: SidebarMenuItem = {
  text: dict.fleetManagement,
  to: '/fleet-management',
  id: SidebarMenuItems.FLEET_MANAGEMENT,
  tag: {
    text: dict.newTag,
  },
  featureFlag: true,
  splitName: 'courier_fleet_management',
}

const MY_ACCOUNT_SIDEBAR_ITEM: SidebarMenuItem = {
  text: dict.myAccount,
  to: '/my-account',
  id: SidebarMenuItems.MY_ACCOUNT,
  icon: HexaIcons.USER,
}

const DYNAMIC_RADIUS_ITEM: SidebarMenuItem = {
  text: dict.dynamicRadius,
  to: '/dynamic-radius',
  id: SidebarMenuItems.DYNAMIC_RADIUS,
  tag: {
    text: dict.newTag,
  },
  featureFlag: true,
  splitName: 'dynamic_radius_beta',
  icon: HexaIcons.DYNAMIC_RADIUS,
}

const PARTNER_GUIDE_SIDEBAR_ITEM: SidebarMenuItem = {
  text: dict.partnerGuide,
  to: URLS.PARTNER_GUIDE,
  id: SidebarMenuItems.PARTNER_GUIDE,
  isExternalLink: true,
}

const FAQ_SIDEBAR_ITEM: SidebarMenuItem = {
  text: dict.faq,
  to: URLS.FAQ,
  id: SidebarMenuItems.FAQ,
  isExternalLink: true,
  icon: HexaIcons.HELP_CIRCLE,
}

const LOGOUT_SIDEBAR_ITEM: SidebarMenuItem = {
  text: dict.logout,
  to: '#',
  onClick: () => doLogout(),
  id: SidebarMenuItems.LOGOUT,
}

export const sidebarItems: SidebarMenuItem[] = [
  HOME_SIDEBAR_ITEM,
  ORDERS_SIDEBAR_ITEM,
  STOCK_SIDEBAR_ITEM,
  PERFORMANCE_SIDEBAR_ITEM,
  FINANCIAL_SIDEBAR_ITEM,
  FLEET_MANAGEMENT_SIDEBAR_ITEM,
  MY_ACCOUNT_SIDEBAR_ITEM,
  DYNAMIC_RADIUS_ITEM,
  PARTNER_GUIDE_SIDEBAR_ITEM,
  FAQ_SIDEBAR_ITEM,
  LOGOUT_SIDEBAR_ITEM,
]

export const newSideBarItems: SidebarMenuItem[] = [
  HOME_SIDEBAR_ITEM,
  PENDING_SIDEBAR_ITEM,
  DATES_AVAILABILITY,
  SCHEDULED_SIDEBAR_ITEM,
  MAP_SIDEBAR_ITEM,
  HISTORY_SIDEBAR_ITEM,
  INVENTORY_SIDEBAR_ITEM,
  ON_SALE_SIDEBAR_ITEM,
  PERFORMANCE_SIDEBAR_ITEM,
  MANAGEMENT_SIDEBAR_ITEM,
  FINANCIAL_REPORT_MENU_ITEM,
  ONLINE_PAYMENT_SIDEBAR_ITEM,
  DYNAMIC_RADIUS_ITEM,
]
